import type { Facet, FilterStand, Filterwaarde, Lijst } from '@xxtract/tabelfilters';
import { useLijstStandStore } from '@/stores/lijstStandStore';
import type { Product } from './completeness.api';

/**
 * spec-tabelfilters story 9: de productenlijst van datakwaliteit, een serverlijst in het geheugen (§8).
 * Dezelfde sleutels als `PRODUCT_LIJST` in `apps/api/src/modules/completeness/product-lijst.ts`; de
 * sleutels zijn de oude `sortBy`-waarden, zodat oude sorteerlinks blijven werken. Categorie is
 * `overig`: de cel toont vijf indicatoren; filteren op categorie blijft via de tabs (paginafilter).
 */

/** Merknaam uit het ondoorzichtige merk-id (`brand-<base64url van de naam>`); onleesbaar id → het id zelf. */
export function merkNaam(id: string): string {
  const match = /^brand-([A-Za-z0-9_-]+)$/.exec(id);
  if (!match) return id;
  try {
    const b64 = match[1]!.replace(/-/g, '+').replace(/_/g, '/');
    const bytes = Uint8Array.from(atob(b64.padEnd(Math.ceil(b64.length / 4) * 4, '=')), (c) => c.charCodeAt(0));
    return new TextDecoder('utf-8', { fatal: true }).decode(bytes).trim() || id;
  } catch {
    return id;
  }
}

export const PRODUCT_LIJST: Lijst<Product> = {
  kolommen: [
    { sleutel: 'name', kopSleutel: 'products.productName', type: 'tekst', urlNaam: 'search', waarde: (p) => `${p.name} (${p.gtin})` },
    // `brandId` in een link is het vaste merk (paginafilter `merkVast`); de kolom heet in links `merk`.
    { sleutel: 'brand', kopSleutel: 'products.brand', type: 'keuzelijst', urlNaam: 'merk', waarde: (p) => p.brandId ?? null, waardeLabel: (id) => merkNaam(id) },
    { sleutel: 'categorie', kopSleutel: 'products.category', type: 'overig', waarde: () => null },
    { sleutel: 'score', kopSleutel: 'products.completeness', type: 'getal', waarde: (p) => p.score },
    { sleutel: 'missingFields', kopSleutel: 'products.missingFields', type: 'getal', waarde: (p) => p.missingRequiredFieldCount ?? null },
    // Besluit Friso 27-09-2026: "aandacht nodig" is een verborgen filter. Geen control, niet in de
    // filterbalk; telt mee voor de reset en alleen de reset wist hem.
    {
      sleutel: 'attention',
      kopSleutel: 'dashboard.incompleteProducts',
      type: 'keuzelijst',
      enkelvoudig: true,
      vasteWaarden: ['1'],
      sorteer: false,
      zichtbaar: () => false,
      waarde: (p) => ((p.missingRequiredFieldCount ?? 0) >= 1 ? '1' : null),
    },
  ],
  standaardSortering: () => [{ sleutel: 'score', richting: 'op' }],
};

export const COMPLEETHEID = ['incomplete', 'complete'] as const;
export const VELDTYPEN = ['required', 'optional'] as const;

/**
 * Paginafilters van de productenlijst: ze houden hun betekenis (`category` = onvolledig in die
 * categorie) en zetten de lijst op pagina 1. `alleenKolomfilters`: `sort`/`page` in een link horen
 * bij de tabel.
 */
export const PRODUCT_PAGINAFILTER: Lijst<unknown> = {
  alleenKolomfilters: true,
  kolommen: [
    { sleutel: 'completeness', kopSleutel: 'products.completeness', type: 'keuzelijst', enkelvoudig: true, vasteWaarden: COMPLEETHEID, waarde: () => null },
    { sleutel: 'category', kopSleutel: 'products.category', type: 'keuzelijst', enkelvoudig: true, waarde: () => null },
    { sleutel: 'fieldType', kopSleutel: 'products.filterByFieldType', type: 'keuzelijst', enkelvoudig: true, vasteWaarden: VELDTYPEN, waarde: () => null },
    // Besluit Friso 27-09-2026: het merk van een dashboardlink legt de lijst vast; niet wisbaar via de reset.
    { sleutel: 'merkVast', kopSleutel: 'products.brand', type: 'keuzelijst', enkelvoudig: true, urlNaam: 'brandId', waarde: () => null },
  ],
};

/** Zichtbare kolommen: tabelkoppen en facetvraag slaan verborgen kolommen (aandacht) over. */
export const ZICHTBARE_KOLOMMEN = PRODUCT_LIJST.kolommen.filter((k) => k.zichtbaar?.({}) ?? true);

/** Geen facet vragen voor een verborgen kolom: er is geen keuzelijst om hem te tonen. */
export function zonderVerborgenFacetten(parameters: Record<string, string>): Record<string, string> {
  if (!parameters.facetten) return parameters;
  const zichtbaar = new Set(ZICHTBARE_KOLOMMEN.map((k) => k.sleutel));
  const facetten = parameters.facetten.split(',').filter((s) => zichtbaar.has(s));
  const { facetten: _alle, ...rest } = parameters;
  return facetten.length > 0 ? { ...rest, facetten: facetten.join(',') } : rest;
}

/** Standsleutels per klant: de tabel en de paginafilters (met de weergave) apart. */
export function productenSleutels(clientId: string | undefined) {
  return { lijst: `producten:${clientId ?? ''}`, pagina: `productenPagina:${clientId ?? ''}` };
}

/** Eerste gekozen waarde van een enkelvoudige paginafilter. */
export function paginafilterWaarde(filters: FilterStand | undefined, sleutel: string): string | null {
  const w = filters?.[sleutel];
  return Array.isArray(w) && typeof w[0] === 'string' ? w[0] : null;
}

/** Paginafilters → API-parameters (`category` heet op de API `categoryId`). */
export function paginafilterParameters(filters: FilterStand | undefined): Record<string, string> {
  const uit: Record<string, string> = {};
  for (const [sleutel, parameter] of [
    ['completeness', 'completeness'],
    ['category', 'categoryId'],
    ['fieldType', 'fieldType'],
    ['merkVast', 'brandId'],
  ] as const) {
    const w = sleutel === 'merkVast' ? gekozenMerk(filters) : paginafilterWaarde(filters, sleutel);
    if (w) uit[parameter] = w;
  }
  return uit;
}

/** Dashboard-merkkeuze: legt het merk van die klant vast (of maakt het los); kolomfilters blijven, pagina 1. */
export function kiesMerk(clientId: string | undefined, merkId: string | null) {
  const { lijst, pagina } = productenSleutels(clientId);
  const store = useLijstStandStore.getState();
  const { merkVast: _oud, ...rest } = store.standen[pagina]?.filters ?? {};
  store.zetPaginafilter(pagina, merkId ? { ...rest, merkVast: [merkId] } : rest);
  store.zet(lijst, { pagina: 1 });
}

/**
 * Het vaste merk in de paginafilters van de productenlijst, anders geen. Een onleesbaar merk-id uit een
 * link (`brandId=foo`) telt niet: anders gaf elke vraag een 400 en wist ook de reset hem niet.
 */
export function gekozenMerk(filters: FilterStand | undefined): string {
  const id = paginafilterWaarde(filters, 'merkVast');
  return id && merkNaam(id) !== id ? id : '';
}

/**
 * Merkkeuzes zoals §3 ze toont: gekozen eerst, dan op merknaam, "(leeg)" onderaan. De server ordent al
 * zo, maar de keuzelijst toont serverfacetten in de volgorde van het antwoord.
 */
export function ordenMerken(keuzes: readonly Facet[], gekozen: Filterwaarde | undefined): Facet[] {
  const kies = new Set(Array.isArray(gekozen) ? gekozen : []);
  const naam = (w: string | null) => (w === null ? '' : merkNaam(w));
  return [...keuzes].sort(
    (a, b) =>
      Number(kies.has(b.waarde)) - Number(kies.has(a.waarde)) ||
      Number(a.waarde === null) - Number(b.waarde === null) ||
      naam(a.waarde).localeCompare(naam(b.waarde), 'nl', { sensitivity: 'base', numeric: true }),
  );
}
